import { useState } from 'react';
import { Turnstile } from '@marsidev/react-turnstile';
import './Contact.css';

const Contact = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    businessName: '',
    phoneNumber: '',
    email: '',
    website: '' // Honeypot field - should remain empty
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [submitStatus, setSubmitStatus] = useState(''); // 'success' or 'error'

  // Turnstile tokens
  const [pageToken, setPageToken] = useState(''); // Token from page load verification
  const [submitToken, setSubmitToken] = useState(''); // Token from form submit verification

  // Phone number formatting function
  const formatPhoneNumber = (value) => {
    // Remove all non-numeric characters
    const phoneNumber = value.replace(/\D/g, '');

    // Don't format if empty
    if (!phoneNumber) return '';

    // Format based on length
    if (phoneNumber.length <= 3) {
      return `(${phoneNumber}`;
    } else if (phoneNumber.length <= 6) {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    } else {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === 'phoneNumber') {
      // Format phone number as user types
      const formattedPhone = formatPhoneNumber(value);
      setFormData(prev => ({
        ...prev,
        [name]: formattedPhone
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const validateForm = () => {
    const requiredFields = ['firstName', 'lastName', 'businessName', 'phoneNumber', 'email'];
    for (let field of requiredFields) {
      if (!formData[field].trim()) {
        return `Please fill in the ${field.replace(/([A-Z])/g, ' $1').toLowerCase()} field.`;
      }
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      return 'Please enter a valid email address.';
    }

    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationError = validateForm();
    if (validationError) {
      setSubmitMessage(validationError);
      setSubmitStatus('error');
      return;
    }

    // Verify Turnstile token exists
    if (!submitToken) {
      setSubmitMessage('Please complete the security verification.');
      setSubmitStatus('error');
      return;
    }

    setIsSubmitting(true);
    setSubmitMessage('');
    setSubmitStatus('');

    try {
      // Send email via Cloudflare Pages Function (Resend API)
      const response = await fetch('/api/send-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          firstName: formData.firstName,
          lastName: formData.lastName,
          businessName: formData.businessName,
          phoneNumber: formData.phoneNumber,
          email: formData.email,
          website: formData.website, // Honeypot field
          pageToken: pageToken, // Page load verification (optional)
          submitToken: submitToken, // Form submit verification (required)
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to send email');
      }

      // Success message
      setSubmitMessage("Thanks! We'll be in touch soon to set up your founding partner account.");
      setSubmitStatus('success');

      // Reset form
      setFormData({
        firstName: '',
        lastName: '',
        businessName: '',
        phoneNumber: '',
        email: '',
        website: '' // Reset honeypot
      });

      // Reset Turnstile tokens for next submission
      setSubmitToken('');

    } catch (error) {
      setSubmitMessage('Sorry, there was an error submitting your form. Please try again.');
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="contact">
      <div className="contact-container">
        <div className="contact-header">
          <h2 className="contact-title">Join as a Founding Partner</h2>
          <p className="contact-subtitle">
            We're onboarding a small group of Chicago shops as founding partners.
            Full access, completely free. We just want your honest feedback.
          </p>
        </div>

        <div className="contact-form-container">
          {submitMessage && (
            <div className={`submit-overlay ${submitStatus}`}>
              <div className="submit-overlay-content">
                <div className={`submit-message ${submitStatus}`}>
                  {submitMessage}
                </div>
                <button
                  className="close-overlay-button"
                  onClick={() => {
                    setSubmitMessage('');
                    setSubmitStatus('');
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          )}
          <form className="contact-form" onSubmit={handleSubmit}>
            <div className="form-section">
              <div className="form-group">
                <label htmlFor="businessName">*Shop name</label>
                <input
                  type="text"
                  id="businessName"
                  name="businessName"
                  value={formData.businessName}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="firstName">*First name</label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="lastName">*Last name</label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="email">*Email</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="phoneNumber">*Phone</label>
                <input
                  type="tel"
                  id="phoneNumber"
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={handleInputChange}
                  placeholder="(123) 456-7890"
                  maxLength="14"
                  pattern="^\(\d{3}\) \d{3}-\d{4}$"
                  title="Please enter phone number in format: (123) 456-7890"
                  required
                />
              </div>

              {/* Honeypot Field - Hidden from users, catches bots */}
              <div style={{ position: 'absolute', left: '-9999px' }} aria-hidden="true">
                <label htmlFor="website">Website</label>
                <input
                  type="text"
                  id="website"
                  name="website"
                  value={formData.website}
                  onChange={handleInputChange}
                  tabIndex="-1"
                  autoComplete="off"
                />
              </div>
            </div>

            {/* Turnstile #2: Form Submit Verification (Visible) */}
            <div style={{ marginTop: '20px', marginBottom: '20px' }}>
              <Turnstile
                siteKey="0x4AAAAAAB_jqx5UPufLw8-x"
                onSuccess={(token) => setSubmitToken(token)}
                onError={() => setSubmitToken('')}
                onExpire={() => setSubmitToken('')}
                options={{
                  theme: 'light',
                  size: 'normal',
                }}
              />
            </div>

            <button type="submit" className="submit-button" disabled={isSubmitting}>
              {isSubmitting ? 'Submitting...' : 'Get Started — Free'}
            </button>
          </form>
        </div>

        <p className="contact-disclaimer">
          *Submitting this form does not guarantee a Dangol partnership.
          We review every submission and reach out to shops that fit the founding partner program.
        </p>

        {/* Turnstile #1: Page Load Verification (Invisible) */}
        <div style={{ position: 'absolute', left: '-9999px', visibility: 'hidden' }} aria-hidden="true">
          <Turnstile
            siteKey="0x4AAAAAAB_jqx5UPufLw8-x"
            onSuccess={(token) => setPageToken(token)}
            onError={() => setPageToken('')}
            onExpire={() => setPageToken('')}
            options={{
              theme: 'light',
              size: 'invisible',
            }}
          />
        </div>
      </div>
    </section>
  );
};

export default Contact;
